import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {Observable} from 'rxjs';

export interface ParamConfig {
  limit?: number;
  offset?: number;
  search?: string;
  ordering?: string;
}

export interface BaseResponse<T> {
  count: number;
  results: T[];
}

export class BaseService<T> {

  constructor(
    public http: HttpClient,
    private apiURL: string
  ) {
  }

  static getHeaders(
    headers?: { [header: string]: string | string[] },
    as_antonymous: boolean = false,
    no_csrf: boolean = false
  ) {
    if (headers === null || headers === undefined) {
      headers = {};
    }

    if (localStorage.getItem('user') !== null) {
      const user: any = JSON.parse(localStorage.getItem('user') as string);
      if (user != null && user.token != null && !as_antonymous) {
        headers['Authorization'] = 'JWT ' + user.token;
      }
    }

    headers['Content-Type'] = 'application/json';
    return new HttpHeaders(headers);
  }

  /**
   * Method to get objects from back-end api
   * @param config config param
   * @returns Observable<T> Object of T
   */
  getCollection(config: ParamConfig): Observable<BaseResponse<T>> {
    let params = new HttpParams();
    const url = this.apiURL;
    for (const key in config) {
      const value: string = String(key);
      const c = config as any;
      if (!(c[value] === null || c[value] === undefined)) {
        params = params.append(key, c[key].toString());
      }
    }

    return this.http.get<BaseResponse<T>>(url, {
      params: params,
      headers: BaseService.getHeaders()
    }).pipe(
      map(res => res)
    );
  }

  /**
   * Method to get objects from back-end api
   * @returns Observable<T> Object Of T
   */
  getAllWithoutParams(): Observable<T[]> {
    const params = new HttpParams();
    return this.http.get<T[]>(this.apiURL, {
      params: params,
    }).pipe(
      map(res => res)
    );
  }

  /**
   * Method to get objects from back-end api
   * @param config Object with params
   * @returns Observable<T>
   */
  getAll(config: ParamConfig): Observable<T[]> {
    let params = new HttpParams();
    const url = this.apiURL;
    for (const key in config) {
      const c = config as any;
      if (!(c[key] === null || c[key] === undefined)) {
        params = params.append(key, c[key].toString());
      }
    }

    return this.http.get<T[]>(url, {
      params: params,
      headers: BaseService.getHeaders()
    }).pipe(
      map(res => res)
    );
  }

  /**
   * Method to get a single object from back-end api
   * @returns Observable<T>
   */
  getWithoutParams(): Observable<T> {
    const url = `${this.apiURL}`;
    return this.http.get<T>(url, {headers: BaseService.getHeaders()});
  }

  /**
   * Method to get a single object from back-end api
   * @param id id id of the object to get
   * @returns Observable<T>
   */
  get(id: number): Observable<T> {
    const url = `${this.apiURL}${id}`;
    return this.http.get<T>(url, {headers: BaseService.getHeaders()});
  }

  /**
   * Given an object, this function makes a request to create a new object if no id
   * is provided. If the object has a fild id, it requests for an update of the object.
   *
   * @param object object object to be created or updated.
   * @returns Observable<T> Object Of T
   */
  post(object: T): Observable<T> {
    let url = this.apiURL;
    const o = object as any;
    if (!(o['id'] === null || o['id'] === undefined)) {
      url = `${this.apiURL}${o['id']}/`;
      return this.http.put<T>(url, object, {headers: BaseService.getHeaders()});
    }
    return this.http.post<T>(url, object, {headers: BaseService.getHeaders()});
  }

  /**
   *
   * @param object Object to save.
   * @param as_antonymous if an anonymous user.
   * @param no_csrf CSRF Token.
   */
  typePost<TY>(object: TY, as_antonymous: boolean = false, no_csrf: boolean = false): Observable<TY> {
    let url = this.apiURL;
    const o = object as any;
    if (!(o['id'] === null || o['id'] === undefined)) {
      url = `${this.apiURL}${o['id']}/`;
      return this.http.put<TY>(url, object, {headers: BaseService.getHeaders(undefined, as_antonymous, no_csrf)});
    }
    return this.http.post<TY>(url, object, {headers: BaseService.getHeaders(undefined, as_antonymous, no_csrf)});
  }

  /**
   * @param object Object to Save
   */
  typePostReturn<TP, TR>(object: TP): Observable<TR> {
    let url = this.apiURL;
    const o = object as any;
    if (!(o['id'] === null || o['id'] === undefined)) {
      url = `${this.apiURL}${o['id']}/`;
      return this.http.put<TR>(url, object, {headers: BaseService.getHeaders()});
    }
    return this.http.post<TR>(url, object, {headers: BaseService.getHeaders()});
  }

  /**
   *
   * @param object Object to Update.
   */
  patch(object: T): Observable<T> {
    const url = `${this.apiURL}${(object as any)['id']}/`;
    return this.http.patch<T>(url, object, {headers: BaseService.getHeaders()});
  }

  /**
   * Method to return params for query params GET
   * @param table table
   * @param fields field
   * @param filter filter
   */
  getQueryTable(table: string, fields: string, filter: string): any {
    return {
      'tabla': table,
      'campos': fields,
      'filtro': filter
    };
  }
}
